/**
 * PDFPreviewOverlay - In-app PDF preview using Mozilla's pdf.js via react-pdf.
 *
 * Renders PDFs using the react-pdf library, which wraps pdfjs-dist.
 * Supports multiple items with arrow navigation in the header.
 *
 * The PDF is loaded from a Uint8Array (via IPC) and rendered to canvas.
 * The pdf.js worker handles decoding and rendering in a background thread.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { FileText } from 'lucide-react'
import { PreviewOverlay } from './PreviewOverlay'
import { CopyButton } from './CopyButton'
import { ItemNavigator } from './ItemNavigator'
import { useTranslation } from '@okclawpro/i18n/react'
import {
  findPdfPageAtOffsets,
  getPdfDevicePixelRatio,
  getPdfAnchoredScrollTop,
  getPdfPageOffsets,
  getPdfWindowLayout,
  PDF_PAGE_GAP,
  PDF_PRELOAD_PAGES,
  shouldMountPdfDocument,
} from './pdf-preview-layout'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Keep the worker import in the same module as react-pdf usage, per react-pdf guidance.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

interface PreviewItem {
  src: string
  label?: string
}

export interface PDFPreviewOverlayProps {
  isOpen: boolean
  onClose: () => void
  /** Absolute file path for the PDF (single item / backward compat) */
  filePath: string
  /** Multiple items for arrow navigation */
  items?: PreviewItem[]
  /** Initial active item index (defaults to 0) */
  initialIndex?: number
  /** Async loader that returns PDF data as Uint8Array */
  loadPdfData: (path: string) => Promise<Uint8Array>
  theme?: 'light' | 'dark'
  embedded?: boolean
}

export function PDFPreviewOverlay({
  isOpen,
  onClose,
  filePath,
  items,
  initialIndex = 0,
  loadPdfData,
  theme = 'light',
  embedded = false,
}: PDFPreviewOverlayProps) {
  const { t } = useTranslation()
  // Normalize: items array or single filePath
  const resolvedItems = useMemo<PreviewItem[]>(() => {
    if (items && items.length > 0) return items
    return [{ src: filePath }]
  }, [items, filePath])

  const [activeIdx, setActiveIdx] = useState(initialIndex)
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null)
  const [pdfDataSource, setPdfDataSource] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const scrollRafRef = useRef<number | null>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const scrollTopRef = useRef(0)
  const anchorPageRef = useRef(0)
  const [pageWidth, setPageWidth] = useState(760)
  const [pageHeights, setPageHeights] = useState<number[]>([])
  const requestIdRef = useRef(0)
  const documentRequestIdRef = useRef(0)

  const activeItem = resolvedItems[activeIdx]

  // Reset index when overlay opens
  useEffect(() => {
    if (isOpen) {
      setActiveIdx(initialIndex)
    }
  }, [isOpen, initialIndex])

  // Load PDF data when overlay opens or active item changes
  useEffect(() => {
    if (!isOpen || !activeItem?.src) return

    const requestId = ++requestIdRef.current
    let cancelled = false
    setIsLoading(true)
    setError(null)
    setPdfData(null)
    setPdfDataSource(null)
    setNumPages(0)

    loadPdfData(activeItem.src)
      .then((data) => {
        if (!cancelled && requestId === requestIdRef.current) {
          setPdfData(data)
          setPdfDataSource(activeItem.src)
          documentRequestIdRef.current = requestId
          setIsLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled && requestId === requestIdRef.current) {
          setError(err instanceof Error ? err.message : t('overlay.pdfLoadFailed'))
          setIsLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [isOpen, activeItem?.src, loadPdfData, t])

  // A single scroll root is supplied by PreviewOverlay. Coalesce scroll events
  // so a fast wheel/trackpad gesture never causes one React render per event.
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const onScroll = () => {
      if (scrollRafRef.current != null) return
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null
        scrollTopRef.current = root.scrollTop
        setScrollTop(root.scrollTop)
      })
    }
    root.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      root.removeEventListener('scroll', onScroll)
      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current)
      scrollRafRef.current = null
    }
  }, [isOpen])

  // Fit pages to the available viewer width. Quantizing to 16px prevents a
  // resize storm (e.g. dragging the drawer edge) from re-rendering every page.
  useEffect(() => {
    const root = scrollRef.current
    if (!root || typeof ResizeObserver === 'undefined') return
    const update = () => {
      const next = Math.max(320, Math.min(1200, Math.floor((root.clientWidth - 32) / 16) * 16))
      setPageWidth(previous => previous === next ? previous : next)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(root)
    return () => observer.disconnect()
  }, [isOpen])

  useEffect(() => {
    setPageHeights([])
  }, [pageWidth])

  useEffect(() => {
    setPageHeights([])
    scrollTopRef.current = 0
    setScrollTop(0)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [activeItem?.src])

  const estimatedPageHeight = Math.round(pageWidth * 1.4142)
  const heights = useMemo(
    () => Array.from({ length: numPages }, (_, index) => pageHeights[index] ?? estimatedPageHeight),
    [numPages, pageHeights, estimatedPageHeight],
  )
  const pageOffsets = useMemo(() => getPdfPageOffsets(heights, PDF_PAGE_GAP), [heights])
  const centerPage = findPdfPageAtOffsets(scrollTop, pageOffsets)
  anchorPageRef.current = centerPage
  const pageWindow = useMemo(
    () => getPdfWindowLayout(heights, pageOffsets, centerPage, PDF_PRELOAD_PAGES),
    [centerPage, heights, pageOffsets],
  )

  const updatePageHeight = useCallback((pageNumber: number, page: { originalWidth?: number; originalHeight?: number }) => {
    if (!page.originalWidth || !page.originalHeight) return
    const height = Math.max(1, Math.round(pageWidth * page.originalHeight / page.originalWidth))
    const pageIndex = pageNumber - 1
    const previousHeight = pageHeights[pageIndex] ?? estimatedPageHeight
    if (previousHeight === height) return

    const anchoredScrollTop = getPdfAnchoredScrollTop(
      scrollTopRef.current,
      pageIndex,
      anchorPageRef.current,
      previousHeight,
      height,
    )
    if (anchoredScrollTop !== scrollTopRef.current) {
      scrollTopRef.current = anchoredScrollTop
      if (scrollRef.current) scrollRef.current.scrollTop = anchoredScrollTop
      setScrollTop(anchoredScrollTop)
    }
    setPageHeights(previous => {
      if (previous[pageIndex] === height) return previous
      const next = [...previous]
      next[pageIndex] = height
      return next
    })
  }, [estimatedPageHeight, pageHeights, pageWidth])

  // react-pdf's default internal-link handler can only find mounted pages.
  // Virtualized targets may be outside the window, so jump to their stable
  // placeholder offset ourselves; the next scroll frame mounts that window.
  const handleItemClick = useCallback(({ pageIndex }: { pageIndex: number }) => {
    const root = scrollRef.current
    if (!root || pageIndex < 0 || pageIndex >= pageOffsets.length) return
    const target = pageOffsets[pageIndex] ?? 0
    root.scrollTo({ top: target, behavior: 'auto' })
    scrollTopRef.current = target
    setScrollTop(target)
  }, [pageOffsets])

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    if (documentRequestIdRef.current === requestIdRef.current) setNumPages(numPages)
  }, [])

  const onDocumentLoadError = useCallback((error: Error) => {
    if (documentRequestIdRef.current === requestIdRef.current) {
      setError(`${t('overlay.pdfLoadFailed')}: ${error.message}`)
    }
  }, [t])

  // Memoize file object to prevent unnecessary re-renders (react-pdf uses === equality)
  const fileObj = useMemo(() =>
    pdfData && pdfDataSource === activeItem?.src ? { data: pdfData } : null,
    [activeItem?.src, pdfData, pdfDataSource]
  )

  // Header actions: item navigation + copy button
  const headerActions = (
    <div className="flex items-center gap-2">
      <ItemNavigator items={resolvedItems} activeIndex={activeIdx} onSelect={setActiveIdx} size="md" />
      <CopyButton content={activeItem?.src || filePath} title={t('overlay.copyPath')} className="bg-background shadow-minimal" />
    </div>
  )

  return (
    <PreviewOverlay
      isOpen={isOpen}
      onClose={onClose}
      theme={theme}
      typeBadge={{
        icon: FileText,
        label: 'PDF',
        variant: 'orange',
      }}
      filePath={activeItem?.src || filePath}
      error={error ? { label: t('overlay.loadFailed'), message: error } : undefined}
      headerActions={headerActions}
      embedded={embedded}
      contentRef={scrollRef}
      contentMode="flow"
    >
      <div className="w-full flex flex-col items-center">
        {isLoading && (
          <div className="text-muted-foreground text-sm">{t('overlay.loadingPdf')}</div>
        )}
        {shouldMountPdfDocument(Boolean(fileObj)) && fileObj && (
          <Document
            key={activeItem?.src}
            file={fileObj}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            onItemClick={handleItemClick}
            loading={<div className="text-muted-foreground text-sm">{t('overlay.rendering')}</div>}
            className="w-full"
          >
            {numPages > 0 && (
              <div className="w-full" style={{ minHeight: pageWindow.totalHeight }}>
                <div style={{ height: pageWindow.topOffset }} aria-hidden="true" />
                {Array.from({ length: Math.max(0, pageWindow.end - pageWindow.start + 1) }, (_, index) => {
                  const pageNumber = pageWindow.start + index + 1
                  return (
                    <div
                      key={pageNumber}
                      className="flex justify-center bg-white"
                      // pageWindow.end is zero-based while pageNumber is
                      // one-based, so this adds gaps only between mounted pages.
                      style={{ height: heights[pageNumber - 1], marginBottom: pageNumber < pageWindow.end + 1 ? PDF_PAGE_GAP : 0 }}
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={pageWidth}
                        devicePixelRatio={getPdfDevicePixelRatio(typeof window === 'undefined' ? 1 : window.devicePixelRatio)}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        className="pdf-page"
                        onLoadSuccess={(page) => updatePageHeight(pageNumber, page)}
                      />
                    </div>
                  )
                })}
                <div style={{ height: pageWindow.bottomOffset }} aria-hidden="true" />
              </div>
            )}
          </Document>
        )}
      </div>
    </PreviewOverlay>
  )
}
